import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="composer-drop"
//
// Adds drag-and-drop file upload to the assistant composer. Files dropped
// anywhere on the controller's element (the form) get appended to the
// `images` file input via DataTransfer. The overlay only shows while a
// drag is over the form, and a small chip lists the queued filenames so
// the user knows their drop landed.
//
// Targets:
//   input    — the file input the dropped files merge into
//   overlay  — the "Drop files here" cue (toggled hidden)
//   queue    — small chip listing currently-queued filenames
export default class extends Controller {
  static targets = ["input", "overlay", "queue"]

  connect() {
    this.dragDepth = 0
    this.refreshQueue()
  }

  // Form-level dragenter/leave fire repeatedly as the cursor moves over
  // child elements; track depth so the overlay only hides on a true exit.
  dragenter(event) {
    if (!this.hasFiles(event)) return
    event.preventDefault()
    this.dragDepth++
    this.showOverlay()
  }

  dragover(event) {
    if (!this.hasFiles(event)) return
    // Required: without preventDefault the browser opens the file in a
    // new tab on drop instead of firing our drop handler.
    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
  }

  dragleave(event) {
    if (!this.hasFiles(event)) return
    this.dragDepth = Math.max(0, this.dragDepth - 1)
    if (this.dragDepth === 0) this.hideOverlay()
  }

  drop(event) {
    if (!this.hasFiles(event)) return
    event.preventDefault()
    this.dragDepth = 0
    this.hideOverlay()

    const dropped = Array.from(event.dataTransfer.files || [])
    if (dropped.length === 0) return
    this.addFiles(dropped)
  }

  // Bound to the file input's `change` event so manual picks also refresh
  // the queue display.
  inputChanged() {
    this.refreshQueue()
  }

  // Bound on the form via data-action="paste->composer-drop#paste".
  // When the user pastes an image (Cmd+V from a screenshot, or paste from
  // the file manager on Android) we route the file into the same hidden
  // input as drag-and-drop. Text paste is left alone — it lands in the
  // textarea naturally.
  paste(event) {
    const items = event.clipboardData?.items
    if (!items || items.length === 0) return
    const files = []
    for (const item of items) {
      if (item.kind !== "file") continue
      const file = item.getAsFile()
      if (file) files.push(file)
    }
    if (files.length === 0) return
    event.preventDefault()
    this.addFiles(files)
  }

  hasFiles(event) {
    return event.dataTransfer && Array.from(event.dataTransfer.types || []).includes("Files")
  }

  addFiles(newFiles) {
    const accept = this.inputTarget.accept || ""
    const acceptedTypes = accept.split(",").map(s => s.trim().toLowerCase()).filter(Boolean)
    const maxBytes = 10 * 1024 * 1024 // 10 MB per file
    const accepted = []
    const rejected = []
    for (const f of newFiles) {
      if (f.size > maxBytes) { rejected.push({ file: f, reason: "Too large (max 10 MB)" }); continue }
      if (acceptedTypes.length > 0 && !this.matchesAccept(f, acceptedTypes)) {
        rejected.push({ file: f, reason: "Type not allowed" }); continue
      }
      accepted.push(f)
    }
    if (accepted.length > 0) {
      const dt = new DataTransfer()
      if (this.inputTarget.files) {
        for (const f of this.inputTarget.files) dt.items.add(f)
      }
      for (const f of accepted) dt.items.add(f)
      this.inputTarget.files = dt.files
    }
    this.refreshQueue(rejected)
  }

  matchesAccept(file, types) {
    for (const t of types) {
      if (t === file.type.toLowerCase()) return true
      if (t.endsWith("/*") && file.type.toLowerCase().startsWith(t.slice(0, -2))) return true
      if (t.startsWith(".") && file.name.toLowerCase().endsWith(t)) return true
    }
    return false
  }

  refreshQueue(rejected = []) {
    if (!this.hasQueueTarget) return
    const files = this.inputTarget.files
    const okCount = files?.length || 0
    if (okCount === 0 && rejected.length === 0) {
      this.queueTarget.hidden = true
      this.queueTarget.innerHTML = ""
      return
    }
    this.queueTarget.hidden = false
    const okNames = Array.from(files || []).map(f => f.name).join(", ")
    let html = ""
    if (okCount > 0) {
      html += `<div>${okCount} file${okCount === 1 ? "" : "s"} queued: ${this.escape(okNames)}</div>`
    }
    if (rejected.length > 0) {
      html += `<div style="color:#b91c1c;margin-top:4px">`
      for (const r of rejected) {
        html += `<div>✗ ${this.escape(r.file.name)} — ${r.reason}</div>`
      }
      html += `</div>`
    }
    this.queueTarget.innerHTML = html
  }

  escape(s) {
    return s.replace(/[&<>'"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" }[c]))
  }

  showOverlay() {
    if (this.hasOverlayTarget) this.overlayTarget.hidden = false
  }

  hideOverlay() {
    if (this.hasOverlayTarget) this.overlayTarget.hidden = true
  }
}
